import React, { useState, useEffect } from 'react';
import PositionIntentChip from './PositionIntentChip';
import DisciplineStateBanner from './DisciplineStateBanner';
import { DisciplineState, DisciplineAction, Intent, intentLabel } from '../../types/discipline';
import { ClosePreview } from '../../types/position';
import { apiService } from '../../services/api';

/**
 * ResolveModal — BAB-78 flow collapse: read → decide → act in ONE routed
 * surface (no more ResolveModal → PositionActionModal two-modal hop).
 *
 * Steps:
 *   'decide' — the BAB-77 read-only view of the card's DisciplineState
 *              (passed down as a prop, never refetched, ZERO engine calls)
 *              plus the Hold / Roll / Close routing buttons.
 *   'close'  — confirmation: the fired rule, the P&L being locked, and a
 *              user fill-price input prefilled from the current mark. When
 *              the mark is stale the input is badged "as of last close —
 *              enter your fill"; the recorded P&L never silently uses a
 *              stale mark (it's either the server's fresh mark or the
 *              user's explicit fill).
 *   'closed' — the shareable Close artifact ("Closed FCX — banked +$X…")
 *              with one-click copy. The vs-holding number is the Real-Yield
 *              block's Option leg (BAB-67 pnl_breakdown buckets); omitted
 *              when the bundle is unavailable — never fabricated.
 *   'roll'   — the roll picker (getRollStrategies), with the row matching
 *              the user's `discipline.roll_target` pre-seeded + highlighted
 *              as "★ Your rule's target". Rows beyond the user's
 *              max_roll_dte_extension are flagged, not hidden.
 *
 * No-advice framing (compliance, hard AC): every verdict string is
 * attributed to "your rule"; the highlighted picker row is "★ Your rule's
 * target", never "recommended" / "framework pick".
 */

interface ResolvePosition {
  id: string;
  ticker: string;
  positionType?: string;  // 'cash_secured_put' uses strike-relative basis
  status?: string;
  currentPrice?: number | null;
  strikePrice: number;
  entryPrice: number;
  entryPremium: number;
  numContracts?: number;
  expiryDate: string;
  daysToExpiration?: number | null;
  currentPremium?: number | null;
  nextEarningsDate?: string | null;
  // Gold v2 phase 3b: the position's gold.v_position figures (glossary names)
  netBasis?: number | null;
  cushion?: number | null;
  optionPnl?: number | null;
  buybackCost?: number | null;
  optionIsStale?: boolean | null;
}

interface ResolveModalProps {
  isOpen: boolean;
  position: ResolvePosition;
  /**
   * The card's live DisciplineState — the SINGLE verdict source.
   * `null`/`undefined` → the fetch failed or hasn't resolved; the modal
   * degrades honestly (no fabricated verdict) and all actions stay available.
   */
  discipline?: DisciplineState | null;
  onClose: () => void;
  /** Commit the close. `usedManualFill` → the parent records the user's
   *  explicit fill (manual path); otherwise the server re-marks from its
   *  freshest live data. Rejections surface as an inline error. */
  onConfirmClose: (payload: { fillPrice: number; usedManualFill: boolean; notes?: string }) => Promise<void>;
  /** "Roll" hands off to the roll order ticket (the one roll UI). */
  onOpenRoll: () => void;
  /** Oct 2026: the rule set (intent) and the "I no longer want this stock" flag
   *  are edited here instead of on the card. Each PATCH returns the fresh
   *  verdict, handed to the page so the card and this modal both update. */
  intent?: Intent;
  thesisBroken?: boolean;
  onIntentChange?: (intent: Intent) => void;
  onThesisBrokenChange?: (thesisBroken: boolean) => void;
  onDisciplineChange?: (discipline: DisciplineState) => void;
}

type FlowStep = 'decide' | 'close' | 'closed';
type ActionCategory = 'hold' | 'roll' | 'close';

/**
 * Map the fired rule's action to the button it corresponds to.
 * 'Let Assign' maps to hold: the user's rule says take NO defensive trade —
 * assignment is the plan, so the no-action button is the match.
 * Data-quality actions (Stale/Insufficient) and a missing discipline map to
 * null: no button is highlighted, because no rule fired.
 */
const matchesRuleCategory = (action: DisciplineAction | undefined): ActionCategory | null => {
  switch (action) {
    case 'Hold':
    case 'Let Assign':
      return 'hold';
    case 'Roll':
      return 'roll';
    case 'Close':
    case 'Close at Loss':
    case 'Close at Debit':
    case 'Close Both':
      return 'close';
    default:
      return null;
  }
};

const signedDollars = (n: number): string =>
  `${n >= 0 ? '+' : '−'}$${Math.abs(n).toFixed(0)}`;

const ResolveModal: React.FC<ResolveModalProps> = ({
  isOpen,
  position,
  discipline,
  onClose,
  onConfirmClose,
  onOpenRoll,
  intent,
  thesisBroken,
  onIntentChange,
  onThesisBrokenChange,
  onDisciplineChange,
}) => {

  // Thesis flag: checking it fires a hard stop, so it takes a confirm step.
  const [thesisStep, setThesisStep] = useState<'idle' | 'confirm'>('idle');
  const [thesisError, setThesisError] = useState<string | null>(null);
  const setThesis = (next: boolean) => {
    setThesisStep('idle');
    setThesisError(null);
    onThesisBrokenChange?.(next);
    apiService
      .updatePositionThesisBroken(position.id, next)
      .then((res) => {
        if (res.discipline) onDisciplineChange?.(res.discipline);
      })
      .catch(() => {
        onThesisBrokenChange?.(!next);
        setThesisError("Couldn't save that. Nothing changed; try again.");
      });
  };

  const [step, setStep] = useState<FlowStep>('decide');

  // ── Close step state ──
  const [fill, setFill] = useState<string>('');
  const [fillTouched, setFillTouched] = useState(false);
  const [closeNotes, setCloseNotes] = useState('');
  const [closeSubmitting, setCloseSubmitting] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // Snapshot taken at confirm time — the position list refreshes (and this
  // position leaves it) while the artifact is still on screen.
  const [artifact, setArtifact] = useState<{
    totalLocked: number | null;
    premiumBanked: number | null;
    vsHolding: number | null;
    ruleName: string | null;
  } | null>(null);

  const isCsp = position.positionType === 'cash_secured_put';
  const contracts = position.numContracts || 1;
  const shares = contracts * 100;

  // Reset the flow whenever the modal (re)opens or targets another position.
  useEffect(() => {
    if (!isOpen) return;
    setStep('decide');
    setFill(position.currentPremium != null ? position.currentPremium.toFixed(2) : '');
    setFillTouched(false);
    setCloseNotes('');
    setCloseSubmitting(false);
    setCloseError(null);
    setCopied(false);
    setArtifact(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, position.id]);

  // A mark whose last trade is before the current session is badged; the
  // fill the user confirms is what gets recorded either way (server flag).
  const markStale = position.optionIsStale !== false;

  // Close preview at the typed fill: the backend's definitions on the gold
  // row (GET /close-preview), never a browser formula.
  const fillNumForPreview = Number(fill);
  const previewKey = fill.trim() !== '' && !isNaN(fillNumForPreview) && fillNumForPreview >= 0 ? fillNumForPreview : null;
  const [preview, setPreview] = useState<ClosePreview | null>(null);
  useEffect(() => {
    if (!isOpen || step !== 'close' || previewKey == null) { setPreview(null); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      apiService.getClosePreview(position.id, previewKey)
        .then((p) => { if (!cancelled) setPreview(p); })
        .catch(() => { if (!cancelled) setPreview(null); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [isOpen, step, previewKey, position.id]);

  if (!isOpen) return null;

  const matched = matchesRuleCategory(discipline?.action);
  const rollTarget = discipline?.roll_target ?? null;
  // Earnings vs the engine's roll: the engine flags it; the date check covers
  // payloads from before the flag existed.
  const earningsIso = position.nextEarningsDate ? String(position.nextEarningsDate).slice(0, 10) : null;
  const rollCrossesEarnings = !!rollTarget && rollTarget.crosses_earnings === true;
  const earningsLabel = earningsIso
    ? new Date(earningsIso + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : null;

  // Expired / assigned positions can't roll the existing leg — suppress.
  const rollSuppressed =
    position.status === 'assigned' ||
    position.status === 'expired' ||
    position.daysToExpiration != null && position.daysToExpiration < 0;

  // ── Close preview (server, at the fill) ──
  const fillNum = Number(fill);
  const fillValid = fill.trim() !== '' && !isNaN(fillNum) && fillNum >= 0;
  const premiumBankedAtFill = preview?.option_pnl_at_fill ?? null;
  const stockLegPnl = isCsp ? null : preview?.stock_pnl ?? null;
  const totalAtFill = preview?.total_pnl_at_fill ?? null;

  // vs-holding = the option leg's P&L on the current mark (glossary option_pnl);
  // CC only — a CSP has no shares to "just hold".
  const vsHolding = !isCsp ? position.optionPnl ?? null : null;

  const handleConfirmClose = async () => {
    if (!fillValid || closeSubmitting) return;
    setCloseSubmitting(true);
    setCloseError(null);
    try {
      await onConfirmClose({
        fillPrice: fillNum,
        // The fill shown in the dialog is always what gets recorded (phase 2a:
        // the server never re-marks a close from live data). usedManualFill
        // only tells the parent whether the user edited it or the mark was stale.
        usedManualFill: fillTouched || markStale,
        notes: closeNotes.trim() || undefined,
      });
      setArtifact({
        totalLocked: totalAtFill,
        premiumBanked: premiumBankedAtFill,
        vsHolding,
        ruleName: matched === 'close' && discipline?.fired_rule ? discipline.fired_rule.name : null,
      });
      setStep('closed');
    } catch (err: any) {
      setCloseError(err?.response?.data?.detail || 'Failed to close the position. Please try again.');
    } finally {
      setCloseSubmitting(false);
    }
  };

  const shareText = artifact
    ? [
        `Closed $${position.ticker}${artifact.totalLocked != null ? ` ${signedDollars(artifact.totalLocked)}` : ''}`,
        artifact.premiumBanked != null ? `premium banked ${signedDollars(artifact.premiumBanked)}` : null,
        artifact.vsHolding != null
          ? (artifact.vsHolding >= 0
            ? `beat just holding the shares by ${signedDollars(artifact.vsHolding)}`
            : `vs just holding the shares: ${signedDollars(artifact.vsHolding)}`)
          : null,
        artifact.ruleName ? `my rule (${artifact.ruleName}) fired — executed` : null,
        'tracked with Babylon',
      ].filter(Boolean).join(' · ')
    : '';

  const handleCopyArtifact = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable (permissions / http) — leave the text visible
      // for manual selection; no fake success state.
    }
  };

  // ── Shared style atoms ──
  const solidStyle = (color: string): React.CSSProperties => ({
    padding: '12px 16px',
    backgroundColor: color,
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    flex: 1,
    position: 'relative',
    minHeight: '44px',
  });

  const outlineStyle = (color: string): React.CSSProperties => ({
    padding: '12px 16px',
    backgroundColor: 'transparent',
    color: `${color}99`,
    border: `1px solid ${color}50`,
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    flex: 1,
    position: 'relative',
    minHeight: '44px',
  });

  const backButton = (
    <button
      onClick={() => setStep('decide')}
      aria-label="Back to decision"
      style={{
        background: 'none',
        border: '1px solid #374151',
        color: '#9CA3AF',
        fontSize: '13px',
        fontWeight: 600,
        cursor: 'pointer',
        padding: '10px 16px',
        borderRadius: '6px',
        minHeight: '44px',
      }}
    >
      ‹ Back
    </button>
  );

  const headerBlock = (chipWord: string | null, chipColor: string) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {chipWord && (
            <span style={{
              fontSize: '11px',
              fontWeight: '700',
              color: chipColor,
              backgroundColor: `${chipColor}15`,
              border: `1px solid ${chipColor}30`,
              padding: '3px 8px',
              borderRadius: '999px',
              letterSpacing: '0.02em',
            }}>
              {chipWord}
            </span>
          )}
          <span style={{ fontSize: '16px', fontWeight: '700', color: '#E5E7EB' }}>
            {position.ticker}
          </span>
        </div>
        <p style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '4px' }}>
          ${position.currentPrice?.toFixed(2)} | Strike ${position.strikePrice} | {position.daysToExpiration ?? "—"} DTE
        </p>
      </div>
      <button
        onClick={onClose}
        aria-label="Close dialog"
        style={{
          background: 'none',
          border: 'none',
          color: '#6B7280',
          fontSize: '18px',
          cursor: 'pointer',
          padding: '12px',
          minWidth: '44px',
          minHeight: '44px',
        }}
      >
        ✕
      </button>
    </div>
  );

  // ────────────────────────────────────────────────────────────────────
  // Step renderers
  // ────────────────────────────────────────────────────────────────────

  const renderDecide = () => (
    <>
      {/* Header with cost-basis line (decide step only) */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '16px', fontWeight: '700', color: '#E5E7EB' }}>
              {position.ticker}
            </span>
          </div>
          <p style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '4px' }}>
            ${position.currentPrice?.toFixed(2)} | Strike ${position.strikePrice} | {position.daysToExpiration ?? "—"} DTE
          </p>
          <p style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '2px' }}>
            {isCsp ? 'Basis if assigned' : 'Net basis'}: <span style={{ color: '#E5E7EB', fontWeight: '600' }}>{position.netBasis != null ? `$${position.netBasis.toFixed(2)}` : '—'}</span>
            <span style={{ color: '#6B7280', marginLeft: '6px' }}>
              (cushion: {position.cushion != null ? `${(position.cushion * 100).toFixed(1)}%` : '—'})
            </span>
          </p>
        </div>
        <button
          onClick={onClose}
          aria-label="Close dialog"
          style={{
            background: 'none',
            border: 'none',
            color: '#6B7280',
            fontSize: '18px',
            cursor: 'pointer',
            padding: '12px',
            minWidth: '44px',
            minHeight: '44px',
          }}
        >
          ✕
        </button>
      </div>

      {/* Your rule — the same verdict band the card shows (same object), boxed */}
      <div style={{ marginBottom: '16px' }}>
        {discipline ? (
          <DisciplineStateBanner
            discipline={discipline}
            ruleSet={intent ? intentLabel(intent, isCsp) : undefined}
            variant="boxed"
            showFlip
          />
        ) : (
          // Honest degradation: the verdict fetch failed. Never invent a
          // state word — say so, and keep every action available.
          <div className="pc-verdict is-boxed is-data">
            <div className="pc-verdict-line">
              <span>Couldn't load your rules for this position. The live P&amp;L on the card is still current, and you can still act below.</span>
            </div>
          </div>
        )}
      </div>

      {/* Rules for this position: the rule set (intent) and the thesis flag */}
      {intent && onIntentChange && (
        <div className="rm-rules">
          <div className="rm-rules-row">
            <span className="rm-rules-l">Rules for this position</span>
            <PositionIntentChip
              positionId={position.id}
              value={intent}
              isPut={isCsp}
              onChange={onIntentChange}
              onDisciplineRefresh={onDisciplineChange}
            />
          </div>
          {thesisStep === 'confirm' ? (
            <div className="rm-thesis-confirm" role="alertdialog" aria-label="Confirm">
              <p>
                Mark {position.ticker} as a stock you no longer want? Your thesis stop fires right away, so your
                rules will ask you to exit.
              </p>
              <div>
                <button type="button" className="rm-btn is-danger" onClick={() => setThesis(true)}>Yes, I no longer want it</button>
                <button type="button" className="rm-btn" onClick={() => setThesisStep('idle')}>Cancel</button>
              </div>
            </div>
          ) : (
            <label className="rm-thesis">
              <input
                type="checkbox"
                checked={!!thesisBroken}
                onChange={(e) => (e.target.checked ? setThesisStep('confirm') : setThesis(false))}
              />
              I no longer want to own this stock
            </label>
          )}
          {thesisError && <p className="rm-error" role="alert">{thesisError}</p>}
        </div>
      )}

      {/* BAB-6 — Roll-vs-Assign decision card. Upgrades the single "rule's
          target" box into a side-by-side: what assignment realizes (and frees)
          vs what the engine's roll costs in LOCKED capital — the number that
          exposes a capital-inefficient roll the per-contract credit hides.
          Facts only; trap flags name the user's OWN rules, never "recommended".
          Falls back to the legacy rule's-target box when the backend payload
          predates `decision`. */}
      {discipline?.decision && (discipline.decision.assignment || discipline.decision.roll) ? (() => {
        const dec = discipline.decision!;
        const a = dec.assignment;
        const r = dec.roll;
        // The intent decides the honest alternative to rolling.
        const altKind: 'assign' | 'buyback' | 'close' =
          intent === 'wheel' ? 'assign' : intent === 'hold' && !isCsp ? 'buyback' : 'close';
        const altCost = position.buybackCost ?? null;   // glossary buyback_cost
        const altPnl = position.optionPnl ?? null;      // glossary option_pnl
        const pct = (n: number | null | undefined): string =>
          n == null ? '—' : `${n >= 0 ? '' : '−'}${Math.abs(n).toFixed(1)}%`;
        const plainDollars = (n: number): string => `$${Math.round(Math.abs(n)).toLocaleString()}`;
        const cell: React.CSSProperties = {
          fontSize: '12px', color: '#D1D5DB', display: 'flex',
          justifyContent: 'space-between', gap: '8px', padding: '3px 0',
        };
        const label: React.CSSProperties = { color: '#9CA3AF' };
        const trapColor = (sev: string) => (sev === 'alert' ? '#EF4444' : '#FCD34D');
        return (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
              <div style={{ fontSize: '11px', color: '#60A5FA', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {altKind === 'assign' ? 'Assignment vs roll' : altKind === 'buyback' ? 'Buy back vs roll' : 'Close vs roll'}
              </div>
              {dec.is_approximate && (
                <span
                  title="Marks are estimated / delayed (API-only). Treat dollar figures as approximate."
                  style={{ fontSize: '10px', color: '#6B7280' }}
                >
                  ≈ approximate
                </span>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              {/* A — the intent's alternative to rolling (v3.1): Keep buys the call
                  back (never assigns), Harvest closes, Cycle takes assignment. */}
              <div style={{ padding: '12px', backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid #374151', borderRadius: '8px' }}>
                {altKind === 'assign' ? (
                  <>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#A78BFA', marginBottom: '6px' }}>A · Take assignment</div>
                    {a ? (
                      <>
                        <div style={cell}><span style={label}>Realized P&amp;L</span><strong style={{ color: a.realized_pnl >= 0 ? '#10B981' : '#EF4444' }}>{signedDollars(a.realized_pnl)}</strong></div>
                        <div style={cell}><span style={label}>Capital freed</span><strong>{plainDollars(a.capital_freed)}</strong></div>
                      </>
                    ) : (
                      <div style={{ fontSize: '12px', color: '#6B7280' }}>
                        {isCsp ? 'A put assignment buys shares — no freed-capital read.' : '—'}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#A78BFA', marginBottom: '6px' }}>
                      {altKind === 'buyback' ? 'A · Buy back (keep shares)' : 'A · Close'}
                    </div>
                    <div style={cell}><span style={label}>Cost</span><strong>{altCost != null ? `−${plainDollars(altCost)}` : '—'}</strong></div>
                    <div style={cell}><span style={label}>Leg P&amp;L</span><strong style={{ color: (altPnl ?? 0) >= 0 ? '#10B981' : '#EF4444' }}>{altPnl != null ? signedDollars(altPnl) : '—'}</strong></div>
                  </>
                )}
              </div>
              {/* B — Best roll (the engine's chosen target) */}
              <div style={{ padding: '12px', backgroundColor: 'rgba(59, 130, 246, 0.05)', border: '1px solid rgba(59, 130, 246, 0.2)', borderRadius: '8px' }}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#60A5FA', marginBottom: '6px' }}>B · Best roll · your rule's target</div>
                {r ? (
                  <>
                    <div style={cell}><span style={label}>Net credit</span><strong style={{ color: r.net_credit >= 0 ? '#10B981' : '#EF4444' }}>{signedDollars(r.net_credit)}</strong></div>
                    <div style={cell}><span style={label}>Yield on locked capital</span><strong>{r.locked_capital_yield_pct == null ? '—' : `${pct(r.locked_capital_yield_pct)}/yr`}</strong></div>
                    <div style={cell}><span style={label}>New max profit</span><strong>{r.new_max_profit == null ? '—' : signedDollars(r.new_max_profit)}</strong></div>
                    <div style={cell}><span style={label}>Post-roll Δ</span><strong>{r.post_roll_delta == null ? '—' : r.post_roll_delta.toFixed(2)}</strong></div>
                    <div style={cell}><span style={label}>Extension</span><strong>{r.dte_extension == null ? '—' : `+${r.dte_extension}d`}</strong></div>
                    {rollCrossesEarnings && (
                      <div style={{ marginTop: '6px', fontSize: '11px', color: '#F59E0B' }}>
                        ⚠ Holds through earnings{earningsLabel ? ` (${earningsLabel})` : ''}: no roll that clears your rules expires before the report.
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: '12px', color: '#6B7280' }}>No roll clears your rules — assignment is the move.</div>
                )}
              </div>
            </div>
            {/* Trap flags — each names the user's OWN threshold (or is arithmetic) */}
            {dec.traps.length > 0 && (
              <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {dec.traps.map((t, i) => (
                  <p key={i} style={{ fontSize: '11px', color: trapColor(t.severity), margin: 0 }}>
                    {t.severity === 'alert' ? '⚠' : '•'} {t.message}
                  </p>
                ))}
              </div>
            )}
            {/* Neutral factual comparison — never a recommendation */}
            {altKind === 'assign' && a && r && r.locked_capital_yield_pct != null && (
              <p style={{ fontSize: '11px', color: '#9CA3AF', fontStyle: 'italic', margin: '8px 0 0' }}>
                Rolling keeps {plainDollars(r.locked_capital)} locked to earn {pct(r.locked_capital_yield_pct)}/yr; assignment frees it.
              </p>
            )}
            {/* Honest below-basis warning carried from the rule's target */}
            {rollTarget && !rollTarget.cost_basis_check && (
              <p style={{ fontSize: '11px', color: '#FCD34D', margin: '8px 0 0' }}>
                ⚠ This roll doesn't clear your cost basis — rolling locks in part of the loss.
              </p>
            )}
          </div>
        );
      })() : rollTarget && (
        <div style={{
          padding: '12px',
          backgroundColor: 'rgba(59, 130, 246, 0.05)',
          border: '1px solid rgba(59, 130, 246, 0.2)',
          borderRadius: '8px',
          marginBottom: '16px',
        }}>
          <div style={{ fontSize: '11px', color: '#60A5FA', fontWeight: '700', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Your rule's target
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', fontSize: '12px', color: '#D1D5DB' }}>
            <div>Strike: <strong>${rollTarget.new_strike}</strong></div>
            <div>Expiry: <strong>{rollTarget.new_expiry}</strong> (+{rollTarget.dte_extension}d)</div>
            <div>Net Credit: <strong style={{ color: rollTarget.expected_credit >= 0 ? '#10B981' : '#EF4444' }}>${rollTarget.expected_credit.toFixed(2)}</strong></div>
            <div>Delta: <strong>{rollTarget.new_delta?.toFixed(2) ?? 'N/A'}</strong></div>
          </div>
          {!rollTarget.cost_basis_check && (
            <p style={{ fontSize: '11px', color: '#FCD34D', margin: '8px 0 0' }}>
              ⚠ This roll doesn't clear your cost basis — rolling locks in part of the loss.
            </p>
          )}
        </div>
      )}

      {/* Action Buttons — Hold needs no further interaction (the card is
          terminal; this surface is the manual override). Roll/Close route
          IN-PLACE to the act step — no second modal. Roll is suppressed for
          expired/assigned positions. */}
      {(() => {
        const matchEyebrow = (
          <span style={{ display: 'block', fontSize: '9px', fontWeight: '700', letterSpacing: '0.05em', color: '#FCD34D', marginBottom: '2px', textTransform: 'uppercase' }}>
            Matches your rule
          </span>
        );

        return (
          <div style={{ display: 'flex', gap: '8px' }}>
            {/* Hold (also the match for Let Assign — no trade is the plan) */}
            <button
              onClick={onClose}
              style={matched === 'hold' ? solidStyle('#374151') : outlineStyle('#9CA3AF')}
            >
              {matched === 'hold' && matchEyebrow}
              Hold
            </button>

            {/* Roll — suppressed when the leg can no longer be rolled */}
            {!rollSuppressed && (
              <button
                onClick={onOpenRoll}
                style={matched === 'roll' ? solidStyle('#3B82F6') : outlineStyle('#3B82F6')}
              >
                {matched === 'roll' && matchEyebrow}
                Roll
              </button>
            )}

            {/* Close */}
            <button
              onClick={() => setStep('close')}
              style={matched === 'close' ? solidStyle('#EF4444') : outlineStyle('#EF4444')}
            >
              {matched === 'close' && matchEyebrow}
              Close
            </button>
          </div>
        );
      })()}
    </>
  );

  const renderClose = () => (
    <>
      {headerBlock('CLOSE', '#EF4444')}

      {/* The rule being executed — always attributed to the user. */}
      <div style={{
        padding: '12px 16px',
        backgroundColor: 'var(--color-bg-tertiary)',
        borderRadius: '8px',
        border: '1px solid var(--color-border-secondary)',
        marginBottom: '16px',
      }}>
        {matched === 'close' && discipline?.fired_rule ? (
          <p style={{ fontSize: '13px', color: '#E5E7EB', lineHeight: '1.5', margin: 0 }}>
            <span style={{ color: '#9CA3AF', fontWeight: 500 }}>Your rule:</span>{' '}
            <span style={{ fontWeight: 600 }}>{discipline.fired_rule.name}</span> fired
            {' — '}
            <span style={{ color: '#D1D5DB' }}>{discipline.fired_rule.formula_human}.</span>
          </p>
        ) : discipline ? (
          <p style={{ fontSize: '13px', color: '#D1D5DB', lineHeight: '1.5', margin: 0 }}>
            Your rules currently read <strong style={{ color: 'var(--color-text-secondary)' }}>{discipline.action}</strong>. You're choosing to close.
          </p>
        ) : (
          <p style={{ fontSize: '13px', color: '#9CA3AF', lineHeight: '1.5', margin: 0 }}>
            Couldn't load your rules. You can still close.
          </p>
        )}
      </div>

      {/* Fill input — prefilled from the current mark; stale → badged, and
          the recorded P&L is the user's explicit fill (never a silent stale
          mark). */}
      <div style={{ marginBottom: '16px' }}>
        <label
          htmlFor="close-fill-input"
          style={{ display: 'block', fontSize: '12px', color: '#D1D5DB', fontWeight: 600, marginBottom: '6px' }}
        >
          Your fill — buyback price per share
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', fontSize: '14px' }}>$</span>
            <input
              id="close-fill-input"
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              value={fill}
              onChange={(e) => { setFill(e.target.value); setFillTouched(true); }}
              aria-describedby="close-fill-freshness"
              style={{
                width: '120px',
                minHeight: '44px',
                padding: '10px 12px 10px 24px',
                backgroundColor: 'var(--color-bg-tertiary)',
                border: `1px solid ${fillValid ? '#374151' : '#EF4444'}`,
                borderRadius: '6px',
                color: '#E5E7EB',
                fontSize: '15px',
                fontWeight: 600,
                outline: 'none',
              }}
            />
          </div>
          <span
            id="close-fill-freshness"
            style={{
              fontSize: '11px',
              fontWeight: 600,
              color: markStale ? '#FCD34D' : '#9CA3AF',
              backgroundColor: markStale ? 'rgba(245, 158, 11, 0.1)' : 'transparent',
              border: markStale ? '1px solid rgba(245, 158, 11, 0.3)' : 'none',
              padding: markStale ? '4px 8px' : 0,
              borderRadius: '6px',
            }}
          >
            {markStale ? 'as of last close — enter your fill' : 'prefilled from the current mark'}
          </span>
        </div>
        {!fillValid && (
          <p style={{ fontSize: '11px', color: '#FCA5A5', margin: '6px 0 0' }}>
            Enter the price you paid to buy back the option (0 if it expired worthless).
          </p>
        )}
      </div>

      {/* What this fill locks in — live preview */}
      <div style={{
        padding: '12px 16px',
        backgroundColor: 'var(--color-bg-tertiary)',
        borderRadius: '8px',
        border: '1px solid #374151',
        marginBottom: '16px',
        fontVariantNumeric: 'tabular-nums',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#D1D5DB', marginBottom: '4px' }}>
          <span>Premium banked ({contracts}x)</span>
          <span style={{ fontWeight: 700, color: premiumBankedAtFill == null ? '#9CA3AF' : premiumBankedAtFill >= 0 ? '#10B981' : '#EF4444' }}>
            {premiumBankedAtFill != null ? signedDollars(premiumBankedAtFill) : '—'}
          </span>
        </div>
        {!isCsp && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#D1D5DB', marginBottom: '4px' }}>
            <span>Stock leg (open)</span>
            <span style={{ fontWeight: 600, color: stockLegPnl == null ? '#9CA3AF' : stockLegPnl >= 0 ? '#10B981' : '#EF4444' }}>
              {stockLegPnl != null ? signedDollars(stockLegPnl) : '—'}
            </span>
          </div>
        )}
        <div style={{ borderTop: '1px solid #374151', margin: '6px 0' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#E5E7EB' }}>
          <span style={{ fontWeight: 600 }}>{isCsp ? 'P&L you lock in' : 'Position P&L at this fill'}</span>
          <span style={{ fontWeight: 700, color: totalAtFill == null ? '#9CA3AF' : totalAtFill >= 0 ? '#10B981' : '#EF4444' }}>
            {totalAtFill != null ? signedDollars(totalAtFill) : '—'}
          </span>
        </div>
        {!isCsp && stockLegPnl != null && (
          <p style={{ fontSize: '10px', color: '#6B7280', margin: '6px 0 0' }}>
            Closing the call doesn't sell your shares — the stock leg stays open and is shown at its current mark.
          </p>
        )}
      </div>

      {/* Notes (optional) */}
      <textarea
        value={closeNotes}
        onChange={(e) => setCloseNotes(e.target.value)}
        placeholder="Notes (optional)…"
        rows={2}
        style={{
          width: '100%',
          padding: '10px 12px',
          backgroundColor: 'var(--color-bg-tertiary)',
          border: '1px solid #374151',
          borderRadius: '6px',
          color: '#E5E7EB',
          fontSize: '12px',
          resize: 'vertical',
          marginBottom: '16px',
          boxSizing: 'border-box',
        }}
      />

      {closeError && (
        <p role="alert" style={{ fontSize: '12px', color: '#FCA5A5', margin: '0 0 12px' }}>
          {closeError}
        </p>
      )}

      <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between' }}>
        {backButton}
        <button
          onClick={handleConfirmClose}
          disabled={!fillValid || closeSubmitting}
          style={{
            ...solidStyle('#EF4444'),
            flex: 'initial',
            minWidth: '180px',
            opacity: !fillValid || closeSubmitting ? 0.5 : 1,
            cursor: !fillValid || closeSubmitting ? 'not-allowed' : 'pointer',
          }}
        >
          {closeSubmitting ? 'Closing…' : `Confirm close at $${fillValid ? fillNum.toFixed(2) : '—'}`}
        </button>
      </div>
    </>
  );

  const renderClosed = () => (
    <>
      {headerBlock('CLOSED', '#10B981')}

      {/* The shareable artifact — screenshot-worthy, one-click copy. */}
      <div style={{
        padding: '24px 20px',
        background: 'linear-gradient(160deg, #0B1220 0%, #111827 60%, #0F1A14 100%)',
        border: '1px solid #1F2937',
        borderRadius: '12px',
        marginBottom: '16px',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.18em', color: '#6B7280', textTransform: 'uppercase', marginBottom: '12px' }}>
          Babylon
        </div>
        <div style={{ fontSize: '18px', fontWeight: 700, color: '#E5E7EB', marginBottom: '6px' }}>
          Closed {position.ticker}
        </div>
        {artifact?.totalLocked != null && (
          <div style={{
            fontSize: '32px',
            fontWeight: 800,
            color: artifact.totalLocked >= 0 ? '#10B981' : '#EF4444',
            fontVariantNumeric: 'tabular-nums',
            marginBottom: '12px',
          }}>
            {signedDollars(artifact.totalLocked)}
          </div>
        )}
        <div style={{ fontSize: '13px', color: '#D1D5DB', lineHeight: 1.7 }}>
          <div>
            Premium banked:{' '}
            <strong style={{ color: (artifact?.premiumBanked ?? 0) >= 0 ? '#10B981' : '#EF4444' }}>
              {artifact?.premiumBanked != null ? signedDollars(artifact.premiumBanked) : "—"}
            </strong>
          </div>
          {/* vs-holding from the Real-Yield block (BAB-67) — omitted when
              the bundle is unavailable, never guessed. */}
          {artifact?.vsHolding != null && (
            <div>
              {artifact.vsHolding >= 0 ? 'Beat just holding the shares by ' : 'vs just holding the shares: '}
              <strong style={{ color: artifact.vsHolding >= 0 ? '#10B981' : '#EF4444' }}>
                {signedDollars(artifact.vsHolding)}
              </strong>
            </div>
          )}
          {artifact?.ruleName && (
            <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '8px', fontStyle: 'italic' }}>
              Your rule — {artifact.ruleName} — fired. Executed.
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={handleCopyArtifact}
          style={{
            ...outlineStyle('#60A5FA'),
            color: copied ? '#10B981' : '#60A5FA',
            borderColor: copied ? '#10B98150' : '#60A5FA50',
          }}
        >
          {copied ? '✓ Copied' : '⧉ Copy to share'}
        </button>
        <button onClick={onClose} style={solidStyle('#374151')}>
          Done
        </button>
      </div>
    </>
  );

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      }}
      onClick={step === 'closed' ? undefined : onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        style={{
          backgroundColor: 'var(--color-bg-secondary)',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '480px',
          width: '90%',
          maxHeight: '85vh',
          overflowY: 'auto',
          border: '1px solid var(--color-border-secondary)',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {step === 'decide' && renderDecide()}
        {step === 'close' && renderClose()}
        {step === 'closed' && renderClosed()}
      </div>
    </div>
  );
};

export default ResolveModal;
